import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

export default function ThreeBackground() {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    // Scene
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, mount.clientWidth / mount.clientHeight, 0.1, 1000);
    camera.position.z = 5;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    // Particles
    const particleCount = 120;
    const positions = new Float32Array(particleCount * 3);
    const colors = new Float32Array(particleCount * 3);
    const sizes = new Float32Array(particleCount);

    const blueColor  = new THREE.Color('#1A3BCC');
    const accentColor = new THREE.Color('#0EA5E9');
    const orangeColor = new THREE.Color('#FF7B00');

    for (let i = 0; i < particleCount; i++) {
      positions[i * 3]     = (Math.random() - 0.5) * 14;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 10;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 8;
      sizes[i] = Math.random() * 4 + 1;

      const pick = Math.random();
      const c = pick < 0.6 ? blueColor : pick < 0.85 ? accentColor : orangeColor;
      colors[i * 3]     = c.r;
      colors[i * 3 + 1] = c.g;
      colors[i * 3 + 2] = c.b;
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.setAttribute('color',    new THREE.BufferAttribute(colors, 3));
    geo.setAttribute('size',     new THREE.BufferAttribute(sizes, 1));

    const mat = new THREE.PointsMaterial({
      size: 0.06,
      vertexColors: true,
      transparent: true,
      opacity: 0.65,
      sizeAttenuation: true,
    });

    const particles = new THREE.Points(geo, mat);
    scene.add(particles);

    // Floating rings
    const ringGroup = new THREE.Group();
    const ringData = [
      { r: 2.2, tube: 0.012, color: '#1A3BCC', opacity: 0.18, rx: 1.2, ry: 0.3 },
      { r: 3.0, tube: 0.008, color: '#0EA5E9', opacity: 0.12, rx: 0.5, ry: 1.1 },
      { r: 1.5, tube: 0.015, color: '#FF7B00', opacity: 0.20, rx: 0.8, ry: 1.8 },
    ];
    ringData.forEach(({ r, tube, color, opacity, rx, ry }) => {
      const tGeo = new THREE.TorusGeometry(r, tube, 16, 100);
      const tMat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity });
      const torus = new THREE.Mesh(tGeo, tMat);
      torus.rotation.x = rx;
      torus.rotation.y = ry;
      ringGroup.add(torus);
    });
    scene.add(ringGroup);

    // Floating spheres
    const spheres = [];
    const sphereData = [
      { pos: [-2, 1.5, -1], r: 0.18, color: '#1A3BCC', opacity: 0.25 },
      { pos: [3, -1, -2],   r: 0.12, color: '#FF7B00', opacity: 0.30 },
      { pos: [0, -2, -1],   r: 0.10, color: '#0EA5E9', opacity: 0.25 },
      { pos: [-3, -1, -1],  r: 0.14, color: '#1A3BCC', opacity: 0.20 },
    ];
    sphereData.forEach(({ pos, r, color, opacity }) => {
      const sGeo = new THREE.SphereGeometry(r, 16, 16);
      const sMat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity });
      const sphere = new THREE.Mesh(sGeo, sMat);
      sphere.position.set(...pos);
      spheres.push({ mesh: sphere, baseY: pos[1], speed: Math.random() * 0.5 + 0.3, phase: Math.random() * Math.PI * 2 });
      scene.add(sphere);
    });

    // Mouse parallax
    let mouseX = 0, mouseY = 0;
    const handleMouse = (e) => {
      mouseX = (e.clientX / window.innerWidth  - 0.5) * 2;
      mouseY = (e.clientY / window.clientHeight - 0.5) * 2;
    };
    window.addEventListener('mousemove', handleMouse);

    // Resize
    const handleResize = () => {
      camera.aspect = mount.clientWidth / mount.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(mount.clientWidth, mount.clientHeight);
    };
    window.addEventListener('resize', handleResize);

    // Animate
    let frame;
    const clock = new THREE.Clock();
    const animate = () => {
      frame = requestAnimationFrame(animate);
      const t = clock.getElapsedTime();

      particles.rotation.y = t * 0.04;
      particles.rotation.x = t * 0.02;
      ringGroup.rotation.y = t * 0.06;
      ringGroup.rotation.x = t * 0.03;

      spheres.forEach(({ mesh, baseY, speed, phase }) => {
        mesh.position.y = baseY + Math.sin(t * speed + phase) * 0.3;
      });

      // Parallax
      camera.position.x += (mouseX * 0.4 - camera.position.x) * 0.05;
      camera.position.y += (-mouseY * 0.4 - camera.position.y) * 0.05;
      camera.lookAt(scene.position);

      renderer.render(scene, camera);
    };
    animate();

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('mousemove', handleMouse);
      window.removeEventListener('resize', handleResize);
      renderer.dispose();
      if (mount.contains(renderer.domElement)) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, []);

  return <div ref={mountRef} id="three-canvas" className="absolute inset-0 pointer-events-none" />;
}
